import { useEffect, useState } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { History, Undo2, GitCompare } from "lucide-react";
import { LinkQuoteCompareModal } from "../quote/LinkQuoteCompareModal";

const ACTION_STYLE = {
  delete: "text-red-700", status_change: "text-amber-700", update: "text-blue-700",
  create: "text-green-700", confirm: "text-green-700", reinstate: "text-[#C79A3B]",
};

export function ActivityPanel({ onOpenBooking }) {
  const [items, setItems] = useState([]);
  const [diffView, setDiffView] = useState(null);

  const load = () => api.get("/admin/audit").then(r => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const reinstate = async (ev) => {
    if (!(await appConfirm({ title: "Reinstate this record?", description: ev.summary, confirmLabel: "Reinstate" }))) return;
    try {
      const r = await api.post(`/admin/audit/${ev.id}/reinstate`);
      toast.success(r.data.summary || "Reinstated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reinstate"); }
  };

  const hasSwapDiff = (ev) => ev.after && ev.after.kind === "link_quote_swap" && Array.isArray(ev.after.diff);

  return (
    <div data-testid="activity-panel">
      <p className="text-sm text-[#6B7280] mb-4">Full history of staff actions. Reversible mistakes (deletions, status changes, settings &amp; staff edits) can be reinstated.</p>
      <div className="space-y-2">
        {items.map(ev => (
          <div key={ev.id} data-testid={`audit-${ev.id}`} className="border border-[#E5E7EB] bg-white p-3 flex items-start gap-3">
            <History size={16} className="text-[#6B7280] mt-1 shrink-0" />
            <div className="flex-1">
              <div className="text-sm">
                <span className={`font-bold uppercase text-xs ${ACTION_STYLE[ev.action] || ""}`}>{ev.action.replace(/_/g, " ")}</span>
                <span className="text-[#6B7280]"> · {ev.entity_type}</span>
                {ev.reference && <button onClick={() => onOpenBooking?.(ev.reference)} className="ml-2 font-mono text-xs underline hover:text-[#C79A3B]">{ev.reference}</button>}
              </div>
              <div className="text-sm">{ev.summary}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">{ev.actor}{ev.actor_role ? ` (${ev.actor_role})` : ""} · {new Date(ev.created_at).toLocaleString("en-GB")}{ev.reverted ? " · REINSTATED" : ""}</div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {hasSwapDiff(ev) && (
                <button data-testid={`audit-view-diff-${ev.id}`} onClick={() => setDiffView(ev.after)} className="de-btn-secondary text-xs"><GitCompare size={14} /> View diff</button>
              )}
              {ev.reversible && !ev.reverted && (
                <button data-testid={`audit-reinstate-${ev.id}`} onClick={() => reinstate(ev)} className="de-btn-secondary text-xs"><Undo2 size={14} /> Reinstate</button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] p-10 text-center text-[#6B7280]">
            <History size={24} className="mx-auto mb-3 opacity-50" /> No activity recorded yet.
          </div>
        )}
      </div>
      <LinkQuoteCompareModal preview={diffView} readOnly onCancel={() => setDiffView(null)} />
    </div>
  );
}
